/**
 * workingDays.ts — R1 (§39.9): working days for England and Wales, on
 * Europe/London CALENDAR days. Weekends and the bank holidays below are
 * skipped; nothing is fetched at runtime and nothing is stored in the
 * database. Days travel as 'YYYY-MM-DD' strings and are stepped with UTC
 * arithmetic on the string's own date, so no machine's zone can shift them;
 * the only zone-aware step is turning an instant into its London day.
 *
 * The list is gov.uk's england-and-wales events
 * (https://www.gov.uk/bank-holidays.json), copied 15 Oct 2026, when it ran to
 * the end of 2028. workingDays.test.ts fails on 1 January of the first year
 * whose NEXT year the list does not cover — refresh it from the same source.
 */
export const ENGLAND_WALES_BANK_HOLIDAYS: readonly string[] = [
  '2025-01-01', '2025-04-18', '2025-04-21', '2025-05-05', '2025-05-26', '2025-08-25', '2025-12-25', '2025-12-26',
  '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25', '2026-08-31', '2026-12-25', '2026-12-28',
  '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28',
  '2028-01-03', '2028-04-14', '2028-04-17', '2028-05-01', '2028-05-29', '2028-08-28', '2028-12-25', '2028-12-26',
]

const HOLIDAYS = new Set(ENGLAND_WALES_BANK_HOLIDAYS)

/** The list covers a whole year when it holds that year's New Year holiday
 *  (1–3 Jan, substitutes included) AND its Boxing Day (26–28 Dec). */
export function coversYear(year: number, list: readonly string[] = ENGLAND_WALES_BANK_HOLIDAYS): boolean {
  const y = String(year)
  const newYear = list.some(d => d >= `${y}-01-01` && d <= `${y}-01-03`)
  const boxingDay = list.some(d => d >= `${y}-12-26` && d <= `${y}-12-28`)
  return newYear && boxingDay
}

export const isBankHoliday = (iso: string): boolean => HOLIDAYS.has(iso)

const dayMs = (iso: string): number => Date.parse(`${iso}T00:00:00Z`)
const isoOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10)

export function isWorkingDay(iso: string): boolean {
  const wd = new Date(dayMs(iso)).getUTCDay()
  return wd !== 0 && wd !== 6 && !isBankHoliday(iso)
}

/** n working days after the day `iso` (n ≥ 1). */
export function addWorkingDaysIso(iso: string, n: number): string {
  let ms = dayMs(iso)
  let left = n
  while (left > 0) {
    ms += 86_400_000
    if (isWorkingDay(isoOf(ms))) left--
  }
  return isoOf(ms)
}

const LONDON = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' })

function londonParts(t: Date): { day: string; hour: number } {
  const p = Object.fromEntries(LONDON.formatToParts(t).map(x => [x.type, x.value]))
  return { day: `${p.year}-${p.month}-${p.day}`, hour: Number(p.hour) }
}

/** The calendar day an instant falls on in London. */
export function londonDay(t: Date): string {
  return londonParts(t).day
}

/** A due DAY as the instant the wire stores: noon in London that day (11:00Z
 *  in summer time, 12:00Z in winter), so every zone in the UK reads it back
 *  as the same day. */
export function noonLondon(iso: string): string {
  const noonUtc = dayMs(iso) + 12 * 3_600_000
  const offsetHours = londonParts(new Date(noonUtc)).hour - 12
  return new Date(noonUtc - offsetHours * 3_600_000).toISOString()
}

/** The London day a stored due instant names; null for a value that is not
 *  a date. */
export function dueDayOf(dueAt: string | null | undefined): string | null {
  if (!dueAt) return null
  const t = new Date(dueAt)
  return Number.isNaN(t.getTime()) ? null : londonDay(t)
}
